import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Save, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReferencesSection from '../../components/ReferencesSection';

import { API_BASE as API } from '../../config/api';

const WORD_ORDER_TYPES = [
  { value: 'SVO', label: 'SVO (Subject-Verb-Object)', desc: 'English, French, Chinese' },
  { value: 'SOV', label: 'SOV (Subject-Object-Verb)', desc: 'Japanese, Korean, Turkish' },
  { value: 'VSO', label: 'VSO (Verb-Subject-Object)', desc: 'Irish, Arabic, Welsh' },
  { value: 'VOS', label: 'VOS (Verb-Object-Subject)', desc: 'Malagasy, Fijian' },
  { value: 'OVS', label: 'OVS (Object-Verb-Subject)', desc: 'Hixkaryana' },
  { value: 'OSV', label: 'OSV (Object-Subject-Verb)', desc: 'Rare' },
  { value: 'free', label: 'Free Word Order', desc: 'Latin, Russian (case-marked)' },
];

const WordOrderPage = () => {
  const { conlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [orderType, setOrderType] = useState('SVO');
  const [description, setDescription] = useState('');
  const [variations, setVariations] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => { fetchData(); }, [conlangId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/conlangs/${conlangId}/word-order`, { withCredentials: true });
      if (res.data) {
        setOrderType(res.data.order_type || res.data.basic_order || 'SVO');
        setDescription(res.data.description || res.data.variation_description || '');
        if (res.data.variations && typeof res.data.variations === 'object') {
          setVariations(Object.entries(res.data.variations).map(([k, v]) => `${k}: ${v}`).join('\n'));
        }
        setNotes(res.data.notes || '');
      }
    } catch { /* no data yet */ }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const variationsObj = {};
      if (variations) {
        variations.split('\n').forEach(line => {
          const [key, ...rest] = line.split(':');
          if (key?.trim() && rest.length) variationsObj[key.trim()] = rest.join(':').trim();
        });
      }
      await axios.post(`${API}/conlangs/${conlangId}/word-order`, {
        order_type: orderType,
        description: description || null,
        variations: variationsObj,
        notes: notes || null,
      }, { withCredentials: true });
      toast.success('Word order saved');
    } catch { toast.error('Failed to save word order'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="word-order-title">Word Order</h2>
          <p className="text-foreground-muted mt-1">Define sentence structure for {conlang?.name}</p>
        </div>
        {canEdit && (
        <Button onClick={handleSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-word-order-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save
        </Button>
        )}
      </div>

      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Primary Word Order</CardTitle>
          <CardDescription>The default arrangement of subject, verb, and object</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={orderType} onValueChange={setOrderType}>
            <SelectTrigger className="bg-background border-border" data-testid="word-order-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-background-paper border-border">
              {WORD_ORDER_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>
                  <span className="font-medium">{t.label}</span>
                  <span className="text-foreground-muted text-xs ml-2">({t.desc})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Description</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe how word order works in your language. Is it strict or flexible? How does topicalization work? What about heavy NP shift?"
            className="bg-background border-border min-h-[150px]"
            data-testid="word-order-description"
          />
        </CardContent>
      </Card>

      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Variations</CardTitle>
          <CardDescription>Context-specific word order changes (one per line: context: order)</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={variations}
            onChange={(e) => setVariations(e.target.value)}
            placeholder={`questions: VSO\nsubordinate clauses: SOV\ntopicalized: OSV`}
            className="bg-background border-border min-h-[100px] font-mono text-sm"
            data-testid="word-order-variations"
          />
        </CardContent>
      </Card>

      <Card className="bg-background-paper border-border">
        <CardHeader>
          <CardTitle className="text-lg text-foreground">Additional Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any other notes about word order, head-directionality, adposition placement, relative clause position..."
            className="bg-background border-border min-h-[100px]"
            data-testid="word-order-notes"
          />
        </CardContent>
      </Card>

      <div className="p-4 rounded-md bg-primary/10 border border-primary/30 flex gap-3">
        <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-primary text-sm">
          <strong>Word Order</strong> describes how sentences are structured. SVO languages like English put the verb between subject and object. SOV languages like Japanese put the verb at the end. Many languages allow variation based on pragmatic context.
        </div>
      </div>
      <ReferencesSection section="syntax" />
    </div>
  );
};

export default WordOrderPage;
